import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Award,
  Calendar,
  Layers,
  BookOpen,
  Save,
  CheckCircle2,
  AlertCircle,
  Building2,
  RefreshCw,
  Users,
  Percent
} from 'lucide-react';

export const SATMarksPage: React.FC = () => {
  const { school, fetchWithAuth } = useAuth();

  const [selectedMonth, setSelectedMonth] = useState('September');
  const [selectedClass, setSelectedClass] = useState<number>(1);
  const [selectedStream, setSelectedStream] = useState<string>('Arts');
  const [selectedSubject, setSelectedSubject] = useState<number>(2);
  const [maximumMarks, setMaximumMarks] = useState<number>(40);

  const [roster, setRoster] = useState<any[]>([]);
  const [submissionStatus, setSubmissionStatus] = useState('NOT_STARTED');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const satPeriods = [
    { month: 'September', name: 'SAT — September 2026', defaultMax: 40 },
    { month: 'July', name: 'SAT 1 — July 2026', defaultMax: 40 },
    { month: 'August', name: 'SAT — August 2026', defaultMax: 40 },
    { month: 'November', name: 'SAT 2 — November 2026', defaultMax: 40 },
    { month: 'January', name: 'SAT 3 — January 2027', defaultMax: 80 }
  ];

  const class10Subjects = [
    { id: 1, name: 'English', code: 'ENG-101' },
    { id: 2, name: 'Mathematics', code: 'MTH-102' },
    { id: 3, name: 'Science', code: 'SCI-103' },
    { id: 7, name: 'Hindi', code: 'HND-104' },
    { id: 8, name: 'Social Science', code: 'SST-105' }
  ];

  const class12SubjectsByStream: Record<string, { id: number; name: string; code: string }[]> = {
    Arts: [
      { id: 1, name: 'English', code: 'ENG-101' },
      { id: 7, name: 'Hindi', code: 'HND-104' },
      { id: 9, name: 'Political Science', code: 'POL-109' },
      { id: 10, name: 'History', code: 'HIS-110' },
      { id: 11, name: 'Geography', code: 'GEO-111' },
      { id: 12, name: 'Economics', code: 'ECO-112' }
    ],
    Science: [
      { id: 1, name: 'English', code: 'ENG-101' },
      { id: 4, name: 'Physics', code: 'PHY-104' },
      { id: 5, name: 'Chemistry', code: 'CHM-105' },
      { id: 2, name: 'Mathematics', code: 'MTH-102' },
      { id: 6, name: 'Biology', code: 'BIO-106' }
    ],
    Commerce: [
      { id: 1, name: 'English', code: 'ENG-101' },
      { id: 2, name: 'Mathematics', code: 'MTH-102' },
      { id: 12, name: 'Economics', code: 'ECO-112' }
    ]
  };

  const currentSubjects = selectedClass === 1
    ? class10Subjects
    : (class12SubjectsByStream[selectedStream] || class12SubjectsByStream.Arts);

  // Keep subject in sync
  useEffect(() => {
    if (!currentSubjects.some(s => s.id === selectedSubject)) {
      setSelectedSubject(currentSubjects[0]?.id || 1);
    }
  }, [selectedClass, selectedStream]);

  const loadRoster = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const query = new URLSearchParams({
        class_id: String(selectedClass),
        stream: selectedClass === 2 ? selectedStream : '',
        subject_id: String(selectedSubject),
        month: selectedMonth,
        assessment_type: 'SAT'
      });
      const res = await fetchWithAuth(`/api/monthly-assessments/roster?${query}`);
      const json = await res.json();
      if (json.success) {
        setRoster(json.data.students || []);
        const resolvedMax = json.data.maximum_marks || (selectedMonth === 'September' ? 40 : 40);
        setMaximumMarks(resolvedMax);
        setSubmissionStatus(json.data.submission_status || 'NOT_STARTED');
      } else {
        setMessage({ type: 'error', text: json.message || 'Failed to load SAT roster.' });
      }
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: 'Error connecting to server.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoster();
  }, [selectedMonth, selectedClass, selectedStream, selectedSubject]);

  const handleMarkChange = (studentId: number, value: string) => {
    setRoster(prev => prev.map(item => {
      if (item.student_id === studentId) {
        return {
          ...item,
          obtained_marks: value,
          is_absent: false,
          mark_status: 'PRESENT'
        };
      }
      return item;
    }));
  };

  const handleStatusChange = (studentId: number, status: 'PRESENT' | 'ABSENT' | 'NOT_APPLICABLE') => {
    setRoster(prev => prev.map(item => {
      if (item.student_id === studentId) {
        if (status === 'ABSENT') {
          return { ...item, is_absent: true, obtained_marks: '', mark_status: 'ABSENT' };
        } else if (status === 'NOT_APPLICABLE') {
          return { ...item, is_absent: false, obtained_marks: '', mark_status: 'NOT_APPLICABLE' };
        } else {
          return { ...item, is_absent: false, mark_status: 'PRESENT' };
        }
      }
      return item;
    }));
  };

  const handleSave = async (isFinal: boolean) => {
    setSaving(true);
    setMessage(null);

    // Validate marks
    for (const st of roster) {
      if (st.mark_status === 'PRESENT' && !st.is_absent && st.obtained_marks !== '' && st.obtained_marks !== undefined) {
        const val = parseFloat(String(st.obtained_marks));
        if (isNaN(val) || val < 0 || val > maximumMarks) {
          setMessage({
            type: 'error',
            text: `Invalid marks for student ${st.student_name} (SRN: ${st.srn}). Marks must be between 0 and ${maximumMarks}.`
          });
          setSaving(false);
          return;
        }
      }
    }

    try {
      const url = isFinal ? '/api/monthly-assessments/submit' : '/api/monthly-assessments/save-draft';
      const res = await fetchWithAuth(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          class_id: selectedClass,
          stream: selectedClass === 2 ? selectedStream : null,
          subject_id: selectedSubject,
          month: selectedMonth,
          assessment_type: 'SAT',
          maximum_marks: maximumMarks,
          marks: roster.map(st => ({
            student_id: st.student_id,
            obtained_marks: st.is_absent || st.mark_status === 'NOT_APPLICABLE' ? null : (st.obtained_marks !== '' ? parseFloat(String(st.obtained_marks)) : null),
            is_absent: st.is_absent ? 1 : 0,
            status: st.mark_status || (st.is_absent ? 'ABSENT' : 'PRESENT')
          }))
        })
      });
      const json = await res.json();
      if (json.success) {
        setSubmissionStatus(isFinal ? 'SUBMITTED' : 'DRAFT');
        setMessage({
          type: 'success',
          text: isFinal ? 'SAT Marks submitted successfully.' : 'SAT Marks saved as draft successfully.'
        });
      } else {
        setMessage({ type: 'error', text: json.message || 'Operation failed.' });
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Network error occurred while saving SAT marks.' });
    } finally {
      setSaving(false);
    }
  };

  const presentCount = roster.filter(r => !r.is_absent && r.mark_status !== 'NOT_APPLICABLE').length;
  const absentCount = roster.filter(r => r.is_absent || r.mark_status === 'ABSENT').length;
  const naCount = roster.filter(r => r.mark_status === 'NOT_APPLICABLE').length;

  return (
    <div className="space-y-6 pb-12">
      {/* Header */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="text-xs font-bold text-indigo-700 uppercase tracking-wider">
            Student Assessment Test (SAT) Examination Portal
          </div>
          <h1 className="text-lg md:text-xl font-bold text-[#0F2C59]">
            SAT Examination Marks Entry & Verification ({selectedMonth} 2026)
          </h1>
          <div className="text-xs text-slate-500 mt-0.5">
            Configured Maximum Marks: <strong className="text-slate-800">{maximumMarks}</strong> • Validation: <strong className="text-slate-800">0 to {maximumMarks}</strong>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <div className="bg-slate-50 px-3 py-1.5 rounded border border-slate-200 text-xs font-bold text-slate-700">
            Status: <span className={submissionStatus === 'SUBMITTED' ? 'text-emerald-700' : submissionStatus === 'DRAFT' ? 'text-amber-700' : 'text-slate-600'}>{submissionStatus}</span>
          </div>
          <button
            onClick={loadRoster}
            className="p-1.5 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded text-slate-700 transition"
            title="Refresh Roster"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {message && (
        <div className={`p-3 rounded-lg border text-xs flex items-start space-x-2 ${
          message.type === 'success' ? 'bg-emerald-50 border-emerald-300 text-emerald-900' : 'bg-rose-50 border-rose-300 text-rose-900'
        }`}>
          {message.type === 'success' ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />}
          <div className="font-semibold">{message.text}</div>
        </div>
      )}

      {/* Selectors Bar */}
      <div className="bg-[#0F2C59] p-4 rounded-lg text-white shadow-md grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="block text-[11px] font-bold text-amber-400 uppercase tracking-wider mb-1 flex items-center space-x-1">
            <Calendar className="w-3.5 h-3.5" />
            <span>1. Assessment Month</span>
          </label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-full bg-[#081B38] border border-slate-700 text-white font-bold text-xs p-2 rounded"
          >
            {satPeriods.map(p => (
              <option key={p.month} value={p.month}>{p.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-[11px] font-bold text-amber-400 uppercase tracking-wider mb-1 flex items-center space-x-1">
            <Layers className="w-3.5 h-3.5" />
            <span>2. Class</span>
          </label>
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(parseInt(e.target.value, 10))}
            className="w-full bg-[#081B38] border border-slate-700 text-white font-bold text-xs p-2 rounded"
          >
            <option value={1}>Class 10</option>
            <option value={2}>Class 12</option>
          </select>
        </div>

        {selectedClass === 2 && (
          <div>
            <label className="block text-[11px] font-bold text-amber-400 uppercase tracking-wider mb-1 flex items-center space-x-1">
              <Layers className="w-3.5 h-3.5" />
              <span>3. Stream</span>
            </label>
            <select
              value={selectedStream}
              onChange={(e) => setSelectedStream(e.target.value)}
              className="w-full bg-[#081B38] border border-slate-700 text-white font-bold text-xs p-2 rounded"
            >
              <option value="Arts">Arts</option>
              <option value="Science">Science</option>
              <option value="Commerce">Commerce</option>
            </select>
          </div>
        )}

        <div>
          <label className="block text-[11px] font-bold text-amber-400 uppercase tracking-wider mb-1 flex items-center space-x-1">
            <BookOpen className="w-3.5 h-3.5" />
            <span>{selectedClass === 2 ? '4.' : '3.'} Subject</span>
          </label>
          <select
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(parseInt(e.target.value, 10))}
            className="w-full bg-[#081B38] border border-slate-700 text-white font-bold text-xs p-2 rounded"
          >
            {currentSubjects.map(s => (
              <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
            ))}
          </select>
        </div>
      </div>

      {/* Roster Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h2 className="text-sm font-bold text-[#0F2C59] flex items-center gap-2">
              <Award className="w-4 h-4 text-indigo-600" />
              <span>SAT Student Marks Roster • Max Marks: {maximumMarks}</span>
            </h2>
            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
              <span>Total Students: <strong className="text-slate-800">{roster.length}</strong></span>
              <span>•</span>
              <span>Present: <strong className="text-emerald-700">{presentCount}</strong></span>
              <span>•</span>
              <span>Absent: <strong className="text-rose-700">{absentCount}</strong></span>
              {naCount > 0 && <span>• N/A: <strong>{naCount}</strong></span>}
            </div>
          </div>

          <div className="flex items-center space-x-2 self-end sm:self-auto">
            <button
              onClick={() => handleSave(false)}
              disabled={saving || roster.length === 0}
              className="flex items-center space-x-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 px-3 py-1.5 rounded font-semibold text-xs transition disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" />
              <span>Save Draft</span>
            </button>

            <button
              onClick={() => handleSave(true)}
              disabled={saving || roster.length === 0}
              className="flex items-center space-x-1.5 bg-[#0F2C59] hover:bg-[#1E40AF] text-white px-3.5 py-1.5 rounded font-semibold text-xs shadow-sm transition disabled:opacity-50"
            >
              <CheckCircle2 className="w-3.5 h-3.5 text-amber-400" />
              <span>Submit SAT Marks</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-slate-500 text-xs flex items-center justify-center space-x-2">
            <RefreshCw className="w-4 h-4 animate-spin text-slate-400" />
            <span>Loading SAT roster from database...</span>
          </div>
        ) : roster.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-xs">
            No students found for the selected Class/Stream/Subject.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-100 text-slate-700 uppercase font-bold text-[11px] border-b border-slate-200">
                <tr>
                  <th className="py-2.5 px-3 text-center w-14">Roll No</th>
                  <th className="py-2.5 px-3 w-28">SRN</th>
                  <th className="py-2.5 px-3.5">Student Name</th>
                  <th className="py-2.5 px-3 w-20">Gender</th>
                  <th className="py-2.5 px-3.5 w-32 text-center">Status</th>
                  <th className="py-2.5 px-3.5 w-36 text-center">Marks (0–{maximumMarks})</th>
                  <th className="py-2.5 px-3.5 text-right w-24">Percentage</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 font-medium">
                {roster.map((st, idx) => {
                  const mNum = parseFloat(String(st.obtained_marks));
                  const isPresent = !st.is_absent && st.mark_status !== 'NOT_APPLICABLE';
                  const pct = isPresent && !isNaN(mNum) && maximumMarks > 0 ? ((mNum / maximumMarks) * 100).toFixed(1) : null;

                  return (
                    <tr key={st.student_id} className={`hover:bg-slate-50 ${st.is_absent ? 'bg-rose-50/40' : st.mark_status === 'NOT_APPLICABLE' ? 'bg-slate-50' : ''}`}>
                      <td className="py-2 px-3 text-center font-bold text-slate-700">{st.roll_number || idx + 1}</td>
                      <td className="py-2 px-3 text-slate-600 font-mono text-[11px]">{st.srn}</td>
                      <td className="py-2 px-3.5 font-bold text-slate-900">{st.student_name}</td>
                      <td className="py-2 px-3 text-slate-600">{st.gender}</td>
                      <td className="py-2 px-3.5 text-center">
                        <select
                          value={st.is_absent ? 'ABSENT' : st.mark_status === 'NOT_APPLICABLE' ? 'NOT_APPLICABLE' : 'PRESENT'}
                          onChange={(e) => handleStatusChange(st.student_id, e.target.value as any)}
                          className={`text-xs font-bold px-2 py-1 rounded border focus:outline-none ${
                            st.is_absent
                              ? 'bg-rose-50 text-rose-800 border-rose-300'
                              : st.mark_status === 'NOT_APPLICABLE'
                              ? 'bg-slate-100 text-slate-700 border-slate-300'
                              : 'bg-emerald-50 text-emerald-800 border-emerald-300'
                          }`}
                        >
                          <option value="PRESENT">Present</option>
                          <option value="ABSENT">Absent</option>
                          <option value="NOT_APPLICABLE">Not Applicable</option>
                        </select>
                      </td>
                      <td className="py-2 px-3.5 text-center">
                        <input
                          type="number"
                          min="0"
                          max={maximumMarks}
                          step="0.5"
                          disabled={st.is_absent || st.mark_status === 'NOT_APPLICABLE'}
                          value={st.is_absent || st.mark_status === 'NOT_APPLICABLE' ? '' : (st.obtained_marks !== null && st.obtained_marks !== undefined ? st.obtained_marks : '')}
                          onChange={(e) => handleMarkChange(st.student_id, e.target.value)}
                          placeholder={st.is_absent ? 'Absent' : st.mark_status === 'NOT_APPLICABLE' ? 'N/A' : `0–${maximumMarks}`}
                          className={`w-28 text-center py-1 px-2 border rounded font-bold text-xs focus:ring-1 focus:ring-[#0F2C59] ${
                            st.is_absent || st.mark_status === 'NOT_APPLICABLE'
                              ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
                              : 'bg-white border-slate-300 text-slate-900'
                          }`}
                        />
                      </td>
                      <td className="py-2 px-3.5 text-right font-extrabold">
                        {st.is_absent ? (
                          <span className="text-rose-600 text-[11px] font-bold">ABSENT</span>
                        ) : st.mark_status === 'NOT_APPLICABLE' ? (
                          <span className="text-slate-400 text-[11px]">N/A</span>
                        ) : pct !== null ? (
                          <span className={Number(pct) >= 60 ? 'text-emerald-700' : Number(pct) >= 40 ? 'text-amber-700' : 'text-rose-700'}>{pct}%</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
